import { getFunctions, httpsCallable } from 'firebase/functions';
import { getToken } from 'firebase/app-check';
import { FirebaseError } from 'firebase/app';
import { collection, doc, onSnapshot, query, QueryConstraint } from 'firebase/firestore';
import app, { appCheck, auth, db } from './config';

const functions = getFunctions(app, 'us-central1');
export const callReferral = async <T = any>(name: string, data: object = {}): Promise<T> => {
  await auth.authStateReady();
  if (name !== 'referralValidate' && !auth.currentUser) {
    throw new FirebaseError('functions/unauthenticated', 'Please sign in to continue.');
  }
  // Resolve attestation before sending the callable. The SDK attaches the cached token.
  // Do not send requests that are known to be missing App Check credentials.
  try { await getToken(appCheck); }
  catch { throw new FirebaseError('referrals/security-check-failed', 'The browser security check could not be completed.'); }
  return (await httpsCallable<object, T>(functions, name)(data)).data;
};
export const listenReferral = (name: string, constraints: QueryConstraint[], next: (rows: any[]) => void, error: (error: Error) => void) =>
  onSnapshot(query(collection(db, name), ...constraints), snap => next(snap.docs.map(d => ({ ...d.data(), id: d.id }))), error);
export const listenMember = (uid: string, next: (value: any) => void, error: (e: Error) => void) =>
  onSnapshot(doc(db, 'referralMembers', uid), snap => next(snap.exists() ? snap.data() : null), error);
export const defaultProgram = {
  enabled: true, leaderboardEnabled: true, launched: false, launchAt: null as string | null,
  foundingCutoff: null as string | null, step: 10, amountMinor: 1000, currency: 'USD', version: 1,
  eligibility: 'Verified email, a minimum account age of 24 hours, and an admin review for genuine community participation. One referral per person. No self-referrals or automated accounts.',
  terms: 'TheKinCoin has not launched. Rewards are estimates, pending and not redeemable. Only verified eligible referrals count. Distribution is subject to final program terms and applicable requirements. Token prices and future market value are not guaranteed. Founding Member status carries no monetary entitlement.'
};
export type ReferralProgram = typeof defaultProgram;
export const listenProgram = (next: (value: ReferralProgram) => void, error: (e: Error) => void) =>
  onSnapshot(doc(db, 'referralSettings', 'program'), snap => next({ ...defaultProgram, ...snap.data() }), error);
export const money = (minor: number, currency = 'USD') => {
  const format = new Intl.NumberFormat(undefined, { style: 'currency', currency });
  return format.format(minor / Math.pow(10, format.resolvedOptions().maximumFractionDigits));
};
const attributionKey = 'kinship.referral.attribution.v1';
export function saveAttribution(value: { ticket: string; expiresAt: number }) { localStorage.setItem(attributionKey, JSON.stringify(value)); }
export function getAttribution(): string | undefined {
  try { const v = JSON.parse(localStorage.getItem(attributionKey) || 'null'); return v?.expiresAt > Date.now() ? v.ticket : undefined; } catch { return undefined; }
}
export function clearAttribution() { localStorage.removeItem(attributionKey); }
